import type { ReactNode } from 'react';

/**
 * Subset of `UseQueryResult` we care about — kept structural so this
 * component is testable without importing React Query.
 */
export interface StateViewQuery<T> {
  isLoading?: boolean;
  isPending?: boolean;
  isError?: boolean;
  error?: unknown;
  data?: T;
}

export interface StateViewProps<T> {
  query: StateViewQuery<T>;
  children: (data: NonNullable<T>) => ReactNode;
  /** Override the default "Loading…" string. */
  loading?: ReactNode;
  /** Override the default empty-state message. */
  empty?: ReactNode;
  /** Predicate for "empty". Default treats `[]`, `null`, `undefined` as empty. */
  isEmpty?: (data: T) => boolean;
}

const css = `
  .ew-state {
    padding: var(--ew-s-4) 0;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    color: var(--ew-n-500);
  }
  .ew-state--error { color: var(--ew-danger); }
  .ew-state--empty { color: var(--ew-n-400); text-align: center; }
`;

function defaultIsEmpty<T>(data: T): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

/**
 * Uniform handling for the three async states (loading / error / empty)
 * around a React Query result. Stops every page from re-rolling its own
 * `isLoading && <p>Loading…</p>` and inconsistent error UI.
 *
 * @example
 *   const q = useQuery({ queryKey: ['jobs'], queryFn: listJobs });
 *   <StateView query={q}>
 *     {(jobs) => <JobTable jobs={jobs} />}
 *   </StateView>
 *
 * @example
 *   // Custom empty-state for a single-object response
 *   <StateView
 *     query={detailQuery}
 *     isEmpty={(d) => !d}
 *     empty="Project not found."
 *   >
 *     {(project) => <ProjectDetail project={project} />}
 *   </StateView>
 */
export default function StateView<T>({
  query,
  children,
  loading = 'Loading…',
  empty = 'Nothing to show yet.',
  isEmpty = defaultIsEmpty,
}: StateViewProps<T>) {
  if (query.isLoading || query.isPending) {
    return (
      <>
        <style>{css}</style>
        <p className="ew-state">{loading}</p>
      </>
    );
  }
  // Show the error state only when there's nothing to render. React Query
  // keeps the previous data when a background refetch fails (e.g. a transient
  // auth hiccup on window focus) — unmounting the children here blew away
  // in-progress form state (typed-but-unsaved notes) across the whole page.
  // Stale data + a quiet retry beats a destructive "Failed to load" swap.
  if (query.isError && query.data === undefined) {
    const msg = query.error instanceof Error ? query.error.message : 'Request failed';
    return (
      <>
        <style>{css}</style>
        <p className="ew-state ew-state--error">Failed to load: {msg}</p>
      </>
    );
  }
  if (query.data === undefined || isEmpty(query.data)) {
    return (
      <>
        <style>{css}</style>
        <p className="ew-state ew-state--empty">{empty}</p>
      </>
    );
  }
  return <>{children(query.data as NonNullable<T>)}</>;
}
